import React, { useEffect, useState } from 'react';
import { Table, Form, Spinner, Alert, Button } from 'react-bootstrap';
import { HttpData, HttpNoData } from '../../../Core';

const ManagerAccessPage = ({ jwt }) => {
    const [managers, setManagers] = useState([]);
    const [places, setPlaces] = useState([]);
    const [accesses, setAccesses] = useState({});
    const [supervisors, setSupervisors] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Функция загрузки данных
    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // 1. Загружаем менеджеров
            const managersResponse = await HttpNoData(
                '/api/v1/managers/access',
                'GET',
                jwt
            );

            if (!managersResponse.statusSuccessful) {
                throw new Error('Не удалось загрузить список менеджеров');
            }

            const managersData = managersResponse.data || [];
            setManagers(managersData);

            const supervisorMap = {};
            managersData.forEach(m => {
                supervisorMap[m.managerId] = m.canViewAllOrders ?? false;
            });
            setSupervisors(supervisorMap);

            // 2. Загружаем все платформы
            const placesResponse = await HttpNoData(
                '/api/v1/products/places/new/all',
                'GET',
                jwt
            );

            if (!placesResponse.statusSuccessful) {
                throw new Error('Не удалось загрузить список платформ');
            }

            setPlaces(placesResponse.data || []);

            // 3. Загружаем доступы для каждого менеджера
            const accessPromises = managersResponse.data.map(async manager => {
                const accessResponse = await HttpNoData(
                    `/api/v1/managers/access/${manager.managerId}`,
                    'GET',
                    jwt
                );

                return {
                    managerId: manager.managerId,
                    placeIds: accessResponse.statusSuccessful ?
                        (accessResponse.data || []) : []
                };
            });

            const accessResults = await Promise.all(accessPromises);

            // Создаем объект доступов
            const accessMap = {};
            accessResults.forEach(result => {
                const managerAccess = {};
                result.placeIds.forEach(placeId => {
                    managerAccess[placeId] = true;
                });
                accessMap[result.managerId] = managerAccess;
            });

            setAccesses(accessMap);

        } catch (error) {
            console.error('Ошибка загрузки:', error);
            setError(error.message || 'Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [jwt]);

    // Группируем платформы
    const marketplacePlaces = places.filter(p => p.isMarketPlace);
    const otherPlaces = places.filter(p => !p.isMarketPlace);

    // Получаем ID маркетплейсов
    const marketplacePlaceIds = marketplacePlaces.map(p => p.id);

    // Проверяем, выбран ли хотя бы один маркетплейс у менеджера
    const hasMarketplaceAccess = (managerId) => {
        if (!accesses[managerId]) return false;
        return marketplacePlaceIds.some(placeId => accesses[managerId][placeId]);
    };

    // Проверяем, выбраны ли ВСЕ маркетплейсы у менеджера
    const hasAllMarketplacesAccess = (managerId) => {
        if (!accesses[managerId]) return false;
        return marketplacePlaceIds.every(placeId => accesses[managerId][placeId]);
    };

    // Обработчик изменения доступа к маркетплейсам
    const handleMarketplaceChange = (managerId, checked) => {
        const newAccesses = { ...accesses };

        if (!newAccesses[managerId]) {
            newAccesses[managerId] = {};
        }

        // Если ставим галочку - добавляем ВСЕ маркетплейсы
        if (checked) {
            marketplacePlaceIds.forEach(placeId => {
                newAccesses[managerId][placeId] = true;
            });
        }
        // Если снимаем - удаляем ВСЕ маркетплейсы
        else {
            marketplacePlaceIds.forEach(placeId => {
                delete newAccesses[managerId][placeId];
            });
        }

        setAccesses(newAccesses);
    };

    // Обработчик изменения доступа к отдельной платформе (не маркетплейс)
    const handleAccessChange = (managerId, placeId, checked) => {
        setAccesses(prev => ({
            ...prev,
            [managerId]: {
                ...prev[managerId],
                [placeId]: checked
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSaveSuccess(false);

        try {
            const savePromises = managers.flatMap(manager => {
                const managerAccesses = accesses[manager.managerId] || {};
                const placeIds = Object.keys(managerAccesses)
                    .filter(placeId => managerAccesses[placeId])
                    .map(placeId => parseInt(placeId));

                return [
                    HttpData(`/api/v1/managers/access/${manager.managerId}`, 'PUT', jwt, placeIds),
                    HttpData(`/api/v1/managers/${manager.managerId}/supervisor`, 'PATCH', jwt, supervisors[manager.managerId] ?? false)
                ];
            });

            await Promise.all(savePromises);
            setSaveSuccess(true);

        } catch (error) {
            console.error('Ошибка сохранения:', error);
            setError('Ошибка при сохранении доступов');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {saveSuccess && (
                <Alert variant="success" className="mb-3" dismissible onClose={() => setSaveSuccess(false)}>
                    Доступы успешно сохранены!
                </Alert>
            )}

            {loading ? (
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Загрузка данных...</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th style={{ minWidth: '200px' }}>Менеджер</th>
                                <th className="text-center" style={{ minWidth: '140px' }}>
                                    Все заказы
                                </th>
                                <th className="text-center" style={{ minWidth: '150px' }}>
                                    Маркетплейсы
                                    <div className="small text-muted">
                                        ({marketplacePlaces.length} платформ)
                                    </div>
                                </th>
                                {otherPlaces.map(place => (
                                    <th key={place.id} className="text-center" style={{ minWidth: '150px' }}>
                                        {place.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {managers.length === 0 ? (
                                <tr>
                                    <td colSpan={otherPlaces.length + 3} className="text-center">
                                        Нет доступных менеджеров
                                    </td>
                                </tr>
                            ) : (
                                managers.map(manager => (
                                    <tr key={manager.managerId}>
                                        <td style={{ fontWeight: 'bold' }}>
                                            {manager.name}
                                        </td>
                                        <td className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                checked={supervisors[manager.managerId] ?? false}
                                                onChange={(e) => setSupervisors(prev => ({
                                                    ...prev,
                                                    [manager.managerId]: e.target.checked
                                                }))}
                                                disabled={saving}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={hasMarketplaceAccess(manager.managerId)}
                                                    onChange={(e) =>
                                                        handleMarketplaceChange(
                                                            manager.managerId,
                                                            e.target.checked
                                                        )
                                                    }
                                                    disabled={saving}
                                                />
                                                {hasMarketplaceAccess(manager.managerId) && (
                                                    <div className="small text-muted mt-1">
                                                        {hasAllMarketplacesAccess(manager.managerId)
                                                            ? `Все (${marketplacePlaces.length})`
                                                            : 'Частично'}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        {otherPlaces.map(place => (
                                            <td key={place.id} className="text-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={accesses[manager.managerId]?.[place.id] || false}
                                                    onChange={(e) =>
                                                        handleAccessChange(
                                                            manager.managerId,
                                                            place.id,
                                                            e.target.checked
                                                        )
                                                    }
                                                    disabled={saving}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    {marketplacePlaces.length > 0 && (
                        <div className="mt-3 p-2 bg-light rounded">
                            <small className="text-muted">
                                <strong>Маркетплейсы включают:</strong> {marketplacePlaces.map(p => p.name).join(', ')}
                            </small>
                        </div>
                    )}

                    <div className="mt-3">
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={loading || saving || managers.length === 0}
                        >
                            {saving ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Сохранение...
                                </>
                            ) : 'Сохранить'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManagerAccessPage;
